import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "../components/ui/dialog";
import {
  Handshake, Plus, Users, DollarSign, TrendingUp, Building2,
  LogOut, CalendarDays, Clock, CheckCircle, AlertCircle, XCircle,
  AlertTriangle, Lightbulb, Calculator, Share2, MessageCircle,
  Target, Star, Copy, ChevronDown, ChevronUp, Smartphone
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WHATSAPP_TEXTS = [
  {
    title: "Apresentacao Inicial",
    text: `Ola! Tudo bem? Sou consultor parceiro do *CiaPonto* - o sistema de ponto eletronico mais completo do mercado!

Sua empresa ainda usa ponto manual ou relogio antigo? Com o CiaPonto voce tem:

- Registro de ponto pelo celular ou tablet
- Foto comprobatoria e biometria facial
- Cerca eletronica anti-fraude
- Relatorios automaticos
- Calculo de horas extras e adicional noturno

Planos a partir de *R$ 159/mes* para ate 5 funcionarios!

Posso te mostrar como funciona? Leva menos de 5 minutos!`
  },
  {
    title: "Follow-up / Interesse",
    text: `Oi! Lembra que conversamos sobre o *CiaPonto*?

So pra reforcar os beneficios:
- *Economia de tempo*: Relatorios prontos automaticamente
- *Sem fraude*: Biometria facial + cerca eletronica
- *Praticidade*: Funciona no celular, tablet ou computador
- *Conformidade*: Tudo dentro da lei trabalhista

Quer que eu faca um cadastro gratuito pra voce testar?`
  },
  {
    title: "Comparativo de Precos",
    text: `Voce sabia que o *CiaPonto* custa menos que um relogio de ponto tradicional?

Veja nossos planos:
- *Basico*: R$ 159/mes (ate 5 func.)
- *Profissional*: R$ 299/mes (ate 10 func.)
- *Enterprise*: R$ 499/mes (ate 30 func.)

E o melhor: *sem taxa de instalacao*, *sem multa de cancelamento* e *suporte completo*!

Quantos funcionarios sua empresa tem? Posso indicar o plano ideal!`
  },
  {
    title: "Para Contabilidades",
    text: `Ola! Voce que e contador, sabia que o *CiaPonto* facilita muito seu trabalho?

Seus clientes podem usar o sistema e voce recebe:
- Relatorios de ponto prontos
- Calculo automatico de horas extras
- Adicional noturno calculado
- Banco de horas completo
- Assinatura digital de folha

E o melhor: podemos fazer uma parceria onde voce indica para seus clientes e todo mundo ganha!

Vamos conversar?`
  }
];

const SALES_TIPS = [
  {
    icon: Target,
    title: "Foque nos problemas",
    description: "Pergunte como a empresa controla o ponto hoje. Se for manual, mostre os riscos trabalhistas e a economia de tempo com o CiaPonto."
  },
  {
    icon: AlertTriangle,
    title: "Evite multas trabalhistas",
    description: "Empresas com mais de 20 funcionarios sao OBRIGADAS por lei a registrar o ponto (CLT Art. 74). Sem controle, a empresa nao tem como provar a jornada e perde quase 100% das acoes trabalhistas. Multas chegam a R$ 4.000 por funcionario e processos por horas extras nao pagas podem custar dezenas de milhares de reais. O CiaPonto gera prova digital com foto, GPS e biometria - protegendo a empresa juridicamente."
  },
  {
    icon: Smartphone,
    title: "Demonstre na pratica",
    description: "Mostre como funciona no seu celular. A demonstracao ao vivo convence mais que qualquer argumento."
  },
  {
    icon: MessageCircle,
    title: "Suporte com especialistas no painel",
    description: "Diferente de outros sistemas, o CiaPonto tem um *chat ao vivo com especialistas em DP/RH* direto dentro do painel do gestor. O cliente nao precisa abrir ticket nem esperar email - tira duvidas trabalhistas, sobre o sistema ou folha em segundos. Esse e um diferencial enorme: mostre isso na demonstracao!"
  },
  {
    icon: Users,
    title: "Fale com o decisor",
    description: "Procure falar diretamente com o dono ou RH. Eles entendem a dor de gerenciar ponto e tomam a decisao."
  },
  {
    icon: Star,
    title: "Use cases de sucesso",
    description: "Mencione que o CiaPonto ja atende empresas de diversos setores. Nomes de referencia geram confianca."
  }
];

const MARKETING_IMAGES = [
  {
    title: "Banner Sistema de Ponto Eletronico",
    url: "https://customer-assets.emergentagent.com/job_48205eac-2fa2-4c5c-b979-2cc84715d33b/artifacts/5jmk77at_a6af5a23-961e-40d4-ba36-fa673d4685cb.jpeg",
    description: "Banner principal - ideal para abertura de conversa"
  },
  {
    title: "Controle de Ponto Simples, Seguro e Completo",
    url: "https://customer-assets.emergentagent.com/job_48205eac-2fa2-4c5c-b979-2cc84715d33b/artifacts/x2memus6_fe04058f-637b-4bf2-a51a-ab0854ce9850.jpeg",
    description: "Mostra o app em acao - destaca conformidade Portaria 671"
  },
  {
    title: "Controle Rapido e Pratico - Web + Mobile",
    url: "https://customer-assets.emergentagent.com/job_48205eac-2fa2-4c5c-b979-2cc84715d33b/artifacts/1lscw4g5_e81aa457-acda-472d-b2ae-32b54c6cb210.jpeg",
    description: "Versatilidade web e app na mesma tela"
  },
  {
    title: "Elimine os Papeis - Departamento Pessoal",
    url: "https://customer-assets.emergentagent.com/job_48205eac-2fa2-4c5c-b979-2cc84715d33b/artifacts/t9la8771_ea6651b4-3a55-45a9-aec0-3b351f633445.jpeg",
    description: "Foque no problema da burocracia tradicional"
  },
  {
    title: "Controle de Ponto Inteligente - Detalhado",
    url: "https://customer-assets.emergentagent.com/job_48205eac-2fa2-4c5c-b979-2cc84715d33b/artifacts/39tiyeaz_b2bc2445-682d-49ac-b8f2-f587380ca624.jpeg",
    description: "Lista completa de beneficios do sistema"
  },
  {
    title: "Controle de Ponto Inteligente - Vertical",
    url: "https://customer-assets.emergentagent.com/job_48205eac-2fa2-4c5c-b979-2cc84715d33b/artifacts/1e9zhwhg_38f0c926-0a45-45b8-980b-be79bbaf3c59.jpeg",
    description: "Versao para Stories - solicite uma demonstracao"
  },
  {
    title: "Planos com Precos - Basico, Profissional e Empresarial",
    url: "https://customer-assets.emergentagent.com/job_48205eac-2fa2-4c5c-b979-2cc84715d33b/artifacts/cclmsmhy_5718a454-d234-4a16-a1c9-a24f805c7b49.jpeg",
    description: "Use para fechar a venda - mostra precos R$ 159, 299, 499"
  },
  {
    title: "Sistema de Ponto - Vertical (Stories)",
    url: "https://customer-assets.emergentagent.com/job_48205eac-2fa2-4c5c-b979-2cc84715d33b/artifacts/rav1jr0t_7a4ffdc2-004d-41c8-983d-2f428988a986.jpeg",
    description: "Formato vertical - ideal para Stories Instagram/WhatsApp"
  },
  {
    title: "Sistema de Ponto - Capa Horizontal",
    url: "https://customer-assets.emergentagent.com/job_48205eac-2fa2-4c5c-b979-2cc84715d33b/artifacts/b9r7s60x_f6361b88-d647-4dc9-90ef-4cda3b0de9ce.jpeg",
    description: "Formato landscape - capa de Facebook ou LinkedIn"
  },
  {
    title: "App em uso - Tela Inicial",
    url: "https://customer-assets.emergentagent.com/job_48205eac-2fa2-4c5c-b979-2cc84715d33b/artifacts/jm0dw3nq_IMG_3830.png",
    description: "Print real do app - dashboard do funcionario"
  },
  {
    title: "App em uso - Solicitacoes",
    url: "https://customer-assets.emergentagent.com/job_48205eac-2fa2-4c5c-b979-2cc84715d33b/artifacts/e92qv2t2_IMG_3831.png",
    description: "Tela de ajuste de ponto e abonos"
  },
  {
    title: "App em uso - Relatorios",
    url: "https://customer-assets.emergentagent.com/job_48205eac-2fa2-4c5c-b979-2cc84715d33b/artifacts/33yd01c9_IMG_3832.png",
    description: "Relatorio mensal de horas e banco de horas"
  },
  {
    title: "App em uso - Menu do Colaborador",
    url: "https://customer-assets.emergentagent.com/job_48205eac-2fa2-4c5c-b979-2cc84715d33b/artifacts/d914vd64_IMG_3833.png",
    description: "Perfil, assinatura de folha, QR Code e mais"
  }
];

export default function AffiliateDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewReferral, setShowNewReferral] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeSection, setActiveSection] = useState("dashboard");
  const [simPlanSelected, setSimPlanSelected] = useState("basico");
  const [expandedTip, setExpandedTip] = useState(null);
  const [newReferral, setNewReferral] = useState({
    company_name: "", responsible_name: "", email: "", cnpj: "", num_employees: 1, plan: "basico"
  });

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem("affiliate_token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  const checkAuth = useCallback(() => {
    const token = localStorage.getItem("affiliate_token");
    const userData = localStorage.getItem("affiliate_user");
    if (!token || !userData) { navigate("/indica/login"); return false; }
    setUser(JSON.parse(userData));
    return true;
  }, [navigate]);

  const fetchDashboard = useCallback(async () => {
    try {
      const [dashRes, refRes] = await Promise.all([
        axios.get(`${API}/affiliate/dashboard`, { headers: getHeaders() }),
        axios.get(`${API}/affiliate/referrals`, { headers: getHeaders() })
      ]);
      setDashboard(dashRes.data);
      setReferrals(refRes.data.referrals);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("affiliate_token");
        localStorage.removeItem("affiliate_user");
        navigate("/indica/login");
      }
    } finally { setLoading(false); }
  }, [getHeaders, navigate]);

  useEffect(() => { if (checkAuth()) fetchDashboard(); }, [checkAuth, fetchDashboard]);

  const handleLogout = () => {
    localStorage.removeItem("affiliate_token");
    localStorage.removeItem("affiliate_user");
    navigate("/indica/login");
  };

  const planOptions = [
    { value: "basico", label: "Basico - R$ 159/mes (ate 5 func.)", price: 159 },
    { value: "profissional", label: "Profissional - R$ 299/mes (ate 10 func.)", price: 299 },
    { value: "enterprise", label: "Enterprise - R$ 499/mes (ate 30 func.)", price: 499 }
  ];

  const handleSubmitReferral = async (e) => {
    e.preventDefault();
    if (!newReferral.company_name || !newReferral.responsible_name || !newReferral.email || !newReferral.cnpj) {
      toast.error("Preencha todos os campos"); return;
    }
    setSubmitting(true);
    try {
      const planMap = { basico: 5, profissional: 10, enterprise: 30 };
      const payload = { ...newReferral, num_employees: planMap[newReferral.plan] || newReferral.num_employees };
      const res = await axios.post(`${API}/affiliate/referrals`, payload, { headers: getHeaders() });
      toast.success("Indicacao registrada! Aguarde aprovacao do admin (ate 2 dias uteis).");
      setShowNewReferral(false);
      setNewReferral({ company_name: "", responsible_name: "", email: "", cnpj: "", num_employees: 1, plan: "basico" });
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao registrar indicacao");
    } finally { setSubmitting(false); }
  };

  const statusBadge = (status) => {
    const config = {
      ativo: { bg: "bg-green-100 text-green-700", icon: <CheckCircle className="w-3.5 h-3.5" />, label: "Ativo" },
      pendente: { bg: "bg-amber-100 text-amber-700", icon: <Clock className="w-3.5 h-3.5" />, label: "Aguardando Aprovacao" },
      cancelado: { bg: "bg-red-100 text-red-700", icon: <XCircle className="w-3.5 h-3.5" />, label: "Cancelado" }
    };
    const c = config[status] || config.pendente;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${c.bg}`}>{c.icon} {c.label}</span>;
  };

  const empresaStatusBadge = (status) => {
    if (status === "paid") return <span className="px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 bg-green-100 text-green-700"><CheckCircle className="w-3.5 h-3.5" /> Em dia</span>;
    return <span className="px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 bg-red-100 text-red-700"><AlertTriangle className="w-3.5 h-3.5" /> Devendo</span>;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Texto copiado! Cole no WhatsApp.");
  };

  // Simulador - por plano escolhido (ganho por 1 empresa)
  const PLANS_CONFIG = {
    basico: { name: "Basico", price: 159, employees: "ate 5 funcionarios" },
    profissional: { name: "Profissional", price: 299, employees: "ate 10 funcionarios" },
    enterprise: { name: "Enterprise", price: 499, employees: "ate 30 funcionarios" }
  };
  const simPlanData = PLANS_CONFIG[simPlanSelected];
  const simCommissionMonthly = simPlanData.price * 0.15;
  const simCommissionAnnual = simCommissionMonthly * 12;

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1CA1BA]"></div></div>;
  }

  const navItems = [
    { id: "dashboard", label: "Painel", icon: TrendingUp },
    { id: "indicacoes", label: "Indicacoes", icon: Building2 },
    { id: "simulador", label: "Simulador", icon: Calculator },
    { id: "dicas", label: "Dicas de Vendas", icon: Lightbulb },
    { id: "materiais", label: "Materiais", icon: Share2 }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#1CA1BA]/10 flex items-center justify-center">
                <Handshake className="w-5 h-5 text-[#1CA1BA]" />
              </div>
              <div>
                <h1 className="font-bold text-slate-900">CiaPonto <span className="text-[#1CA1BA]">Indica+</span></h1>
                <p className="text-xs text-slate-500">{user?.name}</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="affiliate-logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Sair
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto py-2">
            {navItems.map(item => (
              <button key={item.id} onClick={() => setActiveSection(item.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeSection === item.id ? "bg-[#1CA1BA] text-white" : "text-slate-600 hover:bg-slate-100"
                }`} data-testid={`nav-${item.id}`}>
                <item.icon className="w-4 h-4" /> {item.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ===== DASHBOARD ===== */}
        {activeSection === "dashboard" && (
          <div className="space-y-6">
            {/* Aviso */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-testid="approval-notice">
              <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">Aviso sobre aprovacao</p>
                <p className="text-sm text-amber-700 mt-1">Apos cadastrar uma empresa, o administrador tem ate <strong>2 dias uteis</strong> para aprovar o cadastro. Voce sera notificado quando a empresa for ativada.</p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card data-testid="stat-total-referrals">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Users className="w-5 h-5 text-blue-600" /></div>
                    <div><p className="text-2xl font-bold text-slate-900">{dashboard?.total_referrals || 0}</p><p className="text-sm text-slate-500">Total Indicacoes</p></div>
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="stat-active">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-green-600" /></div>
                    <div><p className="text-2xl font-bold text-slate-900">{dashboard?.empresas_em_dia || 0}</p><p className="text-sm text-slate-500">Empresas em Dia</p></div>
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="stat-devendo">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-red-600" /></div>
                    <div><p className="text-2xl font-bold text-slate-900">{dashboard?.empresas_devendo || 0}</p><p className="text-sm text-slate-500">Empresas Devendo</p></div>
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="stat-recurring">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-purple-600" /></div>
                    <div><p className="text-2xl font-bold text-slate-900">R$ {(dashboard?.monthly_recurring || 0).toFixed(2)}</p><p className="text-sm text-slate-500">Receita Mensal</p></div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Payment Info */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-wrap items-center gap-6 text-sm">
                  <div className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-slate-400" /><span className="text-slate-600">Dia de pagamento: <strong>dia {dashboard?.payment_day || 10}</strong></span></div>
                  <div className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-slate-400" /><span className="text-slate-600">PIX: <strong>{dashboard?.pix || "Nao informado"}</strong></span></div>
                  <div className="flex items-center gap-2"><AlertCircle className="w-4 h-4 text-[#1CA1BA]" /><span className="text-slate-600">Comissao: <strong>15% mensal recorrente</strong></span></div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className="hover:shadow-md transition-shadow cursor-pointer border-[#1CA1BA]/20" onClick={() => setShowNewReferral(true)}>
                <CardContent className="pt-6 text-center">
                  <Plus className="w-8 h-8 text-[#1CA1BA] mx-auto mb-2" />
                  <p className="font-medium text-slate-900">Nova Indicacao</p>
                  <p className="text-xs text-slate-500 mt-1">Cadastrar nova empresa</p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveSection("simulador")}>
                <CardContent className="pt-6 text-center">
                  <Calculator className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                  <p className="font-medium text-slate-900">Simulador de Ganhos</p>
                  <p className="text-xs text-slate-500 mt-1">Calcule seus rendimentos</p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveSection("materiais")}>
                <CardContent className="pt-6 text-center">
                  <Share2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                  <p className="font-medium text-slate-900">Material de Divulgacao</p>
                  <p className="text-xs text-slate-500 mt-1">Textos e imagens prontas</p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* ===== INDICACOES ===== */}
        {activeSection === "indicacoes" && (
          <div className="space-y-6">
            {/* Aviso de aprovacao */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
              <Clock className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-blue-700">As empresas cadastradas ficam com status <strong>"Aguardando Aprovacao"</strong> ate o admin confirmar. Prazo: ate <strong>2 dias uteis</strong>.</p>
            </div>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Minhas Indicacoes</CardTitle>
                    <CardDescription>Empresas que voce indicou para o CiaPonto</CardDescription>
                  </div>
                  <Button onClick={() => setShowNewReferral(true)} className="bg-[#1CA1BA] hover:bg-[#17889E]" data-testid="new-referral-btn">
                    <Plus className="w-4 h-4 mr-2" /> Nova Indicacao
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {referrals.length === 0 ? (
                  <div className="text-center py-12">
                    <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 mb-2">Nenhuma indicacao ainda</p>
                    <p className="text-sm text-slate-400">Clique em "Nova Indicacao" para registrar sua primeira empresa</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="referrals-table">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                          <th className="text-left py-3 px-2 font-medium text-slate-600">Empresa</th>
                          <th className="text-left py-3 px-2 font-medium text-slate-600">Responsavel</th>
                          <th className="text-left py-3 px-2 font-medium text-slate-600">Plano</th>
                          <th className="text-left py-3 px-2 font-medium text-slate-600">Comissao/mes</th>
                          <th className="text-left py-3 px-2 font-medium text-slate-600">Pagamento</th>
                          <th className="text-left py-3 px-2 font-medium text-slate-600">Status</th>
                          <th className="text-left py-3 px-2 font-medium text-slate-600">Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        {referrals.map((ref) => (
                          <tr key={ref.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-2"><p className="font-medium text-slate-900">{ref.company_name}</p><p className="text-xs text-slate-500">{ref.cnpj}</p></td>
                            <td className="py-3 px-2 text-slate-600">{ref.responsible_name}</td>
                            <td className="py-3 px-2"><span className="capitalize font-medium">{ref.plan}</span><p className="text-xs text-slate-500">R$ {ref.plan_price?.toFixed(2)}/mes</p></td>
                            <td className="py-3 px-2 font-medium text-purple-600">R$ {ref.commission_recurring?.toFixed(2)}</td>
                            <td className="py-3 px-2">{empresaStatusBadge(ref.empresa_status)}</td>
                            <td className="py-3 px-2">{statusBadge(ref.status)}</td>
                            <td className="py-3 px-2 text-slate-500 text-xs">{ref.created_at ? new Date(ref.created_at).toLocaleDateString("pt-BR") : "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ===== SIMULADOR DE GANHOS ===== */}
        {activeSection === "simulador" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Calculator className="w-5 h-5 text-purple-500" /> Simulador de Ganhos</CardTitle>
                <CardDescription>Escolha um plano e veja quanto voce ganha por <strong>cada empresa indicada</strong></CardDescription>
              </CardHeader>
              <CardContent>
                {/* Seletor de Planos - Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                  {Object.entries(PLANS_CONFIG).map(([key, p]) => (
                    <button
                      key={key}
                      onClick={() => setSimPlanSelected(key)}
                      data-testid={`sim-plan-${key}`}
                      className={`text-left rounded-xl p-4 border-2 transition-all ${
                        simPlanSelected === key
                          ? "border-[#1CA1BA] bg-[#1CA1BA]/5 shadow-md"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-slate-900">{p.name}</span>
                        {simPlanSelected === key && <CheckCircle className="w-5 h-5 text-[#1CA1BA]" />}
                      </div>
                      <p className="text-2xl font-bold text-slate-900">R$ {p.price}<span className="text-sm font-normal text-slate-500">/mes</span></p>
                      <p className="text-xs text-slate-500 mt-1">{p.employees}</p>
                    </button>
                  ))}
                </div>

                {/* Resultado - Por empresa */}
                <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-5 sm:p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Building2 className="w-5 h-5 text-purple-600" />
                    <h3 className="font-bold text-slate-900">Seu ganho por empresa indicada (Plano {simPlanData.name})</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="bg-white rounded-lg p-4 border border-purple-100">
                      <p className="text-xs text-slate-500 mb-1">Comissao Mensal</p>
                      <p className="text-2xl sm:text-3xl font-bold text-purple-700" data-testid="sim-monthly">R$ {simCommissionMonthly.toFixed(2)}</p>
                      <p className="text-xs text-slate-500 mt-1">15% de R$ {simPlanData.price},00</p>
                    </div>
                    <div className="bg-purple-600 rounded-lg p-4 text-white">
                      <p className="text-xs text-purple-100 mb-1">Ganho Anual</p>
                      <p className="text-2xl sm:text-3xl font-bold" data-testid="sim-annual">R$ {simCommissionAnnual.toFixed(2)}</p>
                      <p className="text-xs text-purple-100 mt-1">12x R$ {simCommissionMonthly.toFixed(2)}</p>
                    </div>
                  </div>
                  <div className="mt-4 bg-white/70 rounded-lg p-3 flex items-start gap-2">
                    <TrendingUp className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-slate-600">
                      Comissao <strong>recorrente</strong>: voce recebe todo mes enquanto a empresa estiver ativa e adimplente.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tabela de comissoes por plano - responsiva */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-purple-500" /> Comissoes por Plano
                </CardTitle>
                <CardDescription>Resumo de quanto voce ganha por cada plano vendido</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Desktop: tabela */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Plano</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Valor Mensal</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Sua Comissao (15%)</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Ganho Anual/Empresa</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.values(PLANS_CONFIG).map(p => (
                        <tr key={p.name} className="border-b hover:bg-slate-50">
                          <td className="py-3 px-4 font-medium">{p.name}</td>
                          <td className="py-3 px-4">R$ {p.price},00</td>
                          <td className="py-3 px-4 text-purple-600 font-medium">R$ {(p.price*0.15).toFixed(2)}/mes</td>
                          <td className="py-3 px-4 text-green-600 font-medium">R$ {(p.price*0.15*12).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile: cards empilhados */}
                <div className="md:hidden space-y-3">
                  {Object.values(PLANS_CONFIG).map(p => (
                    <div key={p.name} className="border border-slate-200 rounded-lg p-4 bg-white" data-testid={`mobile-plan-card-${p.name.toLowerCase()}`}>
                      <div className="flex items-center justify-between mb-3 pb-3 border-b border-slate-100">
                        <div>
                          <p className="font-bold text-slate-900">{p.name}</p>
                          <p className="text-xs text-slate-500">{p.employees}</p>
                        </div>
                        <span className="text-lg font-bold text-slate-900">R$ {p.price}<span className="text-xs font-normal text-slate-500">/mes</span></span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-slate-500 mb-0.5">Comissao mensal</p>
                          <p className="text-base font-bold text-purple-600">R$ {(p.price*0.15).toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 mb-0.5">Ganho anual</p>
                          <p className="text-base font-bold text-green-600">R$ {(p.price*0.15*12).toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ===== DICAS DE VENDAS ===== */}
        {activeSection === "dicas" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Lightbulb className="w-5 h-5 text-amber-500" /> Dicas de Vendas</CardTitle>
                <CardDescription>Aprenda as melhores estrategias para indicar o CiaPonto e aumentar seus ganhos</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {SALES_TIPS.map((tip, i) => (
                    <div key={i} className="border rounded-xl p-5 hover:shadow-md transition-shadow hover:border-[#1CA1BA]/30">
                      <div className="w-10 h-10 rounded-lg bg-[#1CA1BA]/10 flex items-center justify-center mb-3">
                        <tip.icon className="w-5 h-5 text-[#1CA1BA]" />
                      </div>
                      <h4 className="font-medium text-slate-900 mb-2">{tip.title}</h4>
                      <p className="text-sm text-slate-600">{tip.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Passo a passo */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Passo a Passo da Indicacao</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { step: 1, title: "Identifique empresas", desc: "Procure empresas que ainda usam controle manual ou relogio de ponto antigo." },
                    { step: 2, title: "Apresente o CiaPonto", desc: "Use os materiais prontos para mostrar os beneficios. Foque nos problemas que o sistema resolve." },
                    { step: 3, title: "Cadastre a empresa", desc: "Com os dados em maos, faca o pre-cadastro aqui no painel. E rapido e facil!" },
                    { step: 4, title: "Aguarde aprovacao", desc: "O admin tem ate 2 dias uteis para aprovar. Voce sera notificado." },
                    { step: 5, title: "Receba sua comissao", desc: "Apos aprovacao, voce recebe 15% mensalmente enquanto o cliente estiver ativo." }
                  ].map(s => (
                    <div key={s.step} className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-[#1CA1BA] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">{s.step}</div>
                      <div><h4 className="font-medium text-slate-900">{s.title}</h4><p className="text-sm text-slate-600">{s.desc}</p></div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ===== MATERIAIS DE DIVULGACAO ===== */}
        {activeSection === "materiais" && (
          <div className="space-y-6">
            {/* Textos prontos para WhatsApp */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><MessageCircle className="w-5 h-5 text-green-500" /> Textos Prontos para WhatsApp</CardTitle>
                <CardDescription>Copie e envie diretamente para seus contatos</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {WHATSAPP_TEXTS.map((msg, i) => (
                    <div key={i} className="border rounded-lg overflow-hidden">
                      <button onClick={() => setExpandedTip(expandedTip === i ? null : i)}
                        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                            <MessageCircle className="w-4 h-4 text-green-600" />
                          </div>
                          <span className="font-medium text-slate-900">{msg.title}</span>
                        </div>
                        {expandedTip === i ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </button>
                      {expandedTip === i && (
                        <div className="px-4 pb-4 border-t">
                          <pre className="whitespace-pre-wrap text-sm text-slate-700 bg-slate-50 rounded-lg p-4 mt-3 font-sans">{msg.text}</pre>
                          <Button onClick={() => copyToClipboard(msg.text)} className="mt-3 bg-green-600 hover:bg-green-700" size="sm" data-testid={`copy-whatsapp-${i}`}>
                            <Copy className="w-4 h-4 mr-2" /> Copiar Texto
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Imagens para compartilhar */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Share2 className="w-5 h-5 text-blue-500" /> Imagens para Compartilhar</CardTitle>
                <CardDescription>Toque/segure a imagem para salvar no celular ou clique em "Abrir" para baixar</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {MARKETING_IMAGES.map((img, i) => (
                    <div key={i} className="border rounded-lg overflow-hidden bg-white hover:shadow-lg transition-shadow">
                      <div className="relative aspect-square bg-slate-100 overflow-hidden">
                        <img src={img.url} alt={img.title} loading="lazy" className="w-full h-full object-cover" />
                      </div>
                      <div className="p-3">
                        <p className="font-medium text-slate-900 text-sm line-clamp-1">{img.title}</p>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{img.description}</p>
                        <div className="flex gap-2 mt-3">
                          <a href={img.url} target="_blank" rel="noopener noreferrer"
                            className="flex-1 text-center bg-[#1CA1BA] text-white text-xs font-medium py-2 px-2 rounded hover:bg-[#17889E] transition-colors"
                            data-testid={`open-image-${i}`}>
                            Abrir / Salvar
                          </a>
                          <button onClick={() => { navigator.clipboard.writeText(img.url); toast.success("Link copiado!"); }}
                            className="px-2 py-2 border border-slate-200 rounded text-slate-600 hover:bg-slate-50 transition-colors"
                            data-testid={`copy-image-${i}`}
                            title="Copiar link">
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* New Referral Dialog */}
      <Dialog open={showNewReferral} onOpenChange={setShowNewReferral}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nova Indicacao</DialogTitle>
            <DialogDescription>Cadastre os dados da empresa. O admin tem ate 2 dias uteis para aprovar.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitReferral} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome da Empresa *</Label>
              <Input value={newReferral.company_name} onChange={(e) => setNewReferral({ ...newReferral, company_name: e.target.value })}
                placeholder="Razao Social" data-testid="referral-company-name" />
            </div>
            <div className="space-y-2">
              <Label>Nome do Responsavel *</Label>
              <Input value={newReferral.responsible_name} onChange={(e) => setNewReferral({ ...newReferral, responsible_name: e.target.value })}
                placeholder="Nome completo" data-testid="referral-responsible-name" />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={newReferral.email} onChange={(e) => setNewReferral({ ...newReferral, email: e.target.value })}
                placeholder="email@empresa.com" data-testid="referral-email" />
            </div>
            <div className="space-y-2">
              <Label>CNPJ *</Label>
              <Input value={newReferral.cnpj} onChange={(e) => setNewReferral({ ...newReferral, cnpj: e.target.value })}
                placeholder="00.000.000/0000-00" data-testid="referral-cnpj" />
            </div>
            <div className="space-y-2">
              <Label>Plano desejado *</Label>
              <select value={newReferral.plan} onChange={(e) => setNewReferral({ ...newReferral, plan: e.target.value })}
                className="w-full border border-slate-200 rounded-md p-2 text-sm" data-testid="referral-plan-select">
                {planOptions.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
              <p className="text-xs text-slate-500">Sua comissao: R$ {(planOptions.find(p => p.value === newReferral.plan)?.price * 0.15 || 0).toFixed(2)}/mes</p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-700 flex items-start gap-2">
                <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
                Apos o cadastro, o administrador tem ate <strong>2 dias uteis</strong> para aprovar. O status ficara como "Aguardando Aprovacao".
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowNewReferral(false)} className="flex-1">Cancelar</Button>
              <Button type="submit" className="flex-1 bg-[#1CA1BA] hover:bg-[#17889E]" disabled={submitting} data-testid="referral-submit-btn">
                {submitting ? "Registrando..." : "Registrar Indicacao"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
